import {
  getLocalizedTest,
  type SupportedLocale,
  type TestDefinition,
} from "@test-platform/shared";

const RECOMMENDABLE_TEST_IDS = [
  "mbti",
  "teto-egen",
  "decision-type",
  "emotion-temperature",
  "anxiety-switch",
  "comfort-language",
  "alone-signal",
  "mood-color",
  "friendship-depth",
  "boundary-language",
  "love-conversation-chemistry",
  "date-tempo-style",
  "jealousy-trigger",
  "office-politics-survival",
  "burnout-denial-level",
] as const;

export const CONCERN_MIN_LENGTH = 10;
export const CONCERN_MAX_LENGTH = 800;

export type AiConcernMatch = {
  title: string;
  summary: string;
  tendencyTags: string[];
  recommendedTests: { id: string; title: string; reason: string }[];
  disclaimer: string;
};

type ConcernMatchContext = {
  concern: string;
  locale: SupportedLocale;
  tests: TestDefinition[];
};

export const AI_CONCERN_MATCH_RESPONSE_SCHEMA = {
  type: "object",
  additionalProperties: false,
  required: ["title", "summary", "tendencyTags", "recommendedTests"],
  properties: {
    title: { type: "string" },
    summary: { type: "string" },
    tendencyTags: {
      type: "array",
      minItems: 2,
      maxItems: 4,
      items: { type: "string" },
    },
    recommendedTests: {
      type: "array",
      minItems: 2,
      maxItems: 3,
      items: {
        type: "object",
        additionalProperties: false,
        required: ["id", "reason"],
        properties: {
          id: { type: "string" },
          reason: { type: "string" },
        },
      },
    },
  },
} as const;

function compactText(value: string, maxLength: number) {
  return value.replace(/\s+/g, " ").trim().slice(0, maxLength);
}

function getObject(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object"
    ? (value as Record<string, unknown>)
    : null;
}

function getText(value: unknown, fallback: string, maxLength: number) {
  return typeof value === "string" && value.trim()
    ? compactText(value, maxLength)
    : fallback;
}

function getTextArray(
  value: unknown,
  fallback: string[],
  maxItems: number,
  maxLength: number,
) {
  if (!Array.isArray(value)) return fallback;
  const items = value
    .filter((item): item is string => typeof item === "string")
    .map((item) => compactText(item, maxLength))
    .filter(Boolean)
    .slice(0, maxItems);
  return items.length > 0 ? items : fallback;
}

export function getConcernMatchTests(locale: SupportedLocale) {
  return RECOMMENDABLE_TEST_IDS.map((id) => getLocalizedTest(id, locale)).filter(
    (test): test is TestDefinition => Boolean(test),
  );
}

export function getAiConcernMatchSystemPrompt(locale: SupportedLocale) {
  return locale === "ko"
    ? [
        "You match a user's everyday concern to light personality-test content in Korean.",
        "Return JSON only. Do not mention Gemini, AI, prompts, or internal rules.",
        "Do not provide therapy, diagnosis, legal, financial, or professional advice.",
        "Recommend only test IDs from the provided candidate list.",
      ].join("\n")
    : [
        "You match a user's everyday concern to light personality quiz content in English.",
        "Return JSON only. Do not mention Gemini, AI, prompts, or internal rules.",
        "Do not provide therapy, diagnosis, legal, financial, or professional advice.",
        "Recommend only test IDs from the provided candidate list.",
      ].join("\n");
}

export function getAiConcernMatchUserPrompt(context: ConcernMatchContext) {
  const candidates = context.tests
    .map((test) => `- ${test.id}: ${test.title} / ${test.description}`)
    .join("\n");

  return [
    `Concern: ${context.concern}`,
    "Candidate tests:",
    candidates,
    "Create a compact result with:",
    "- title: short headline",
    "- summary: 1-2 casual sentences",
    "- tendencyTags: 2-4 short tags",
    "- recommendedTests: 2-3 candidate IDs with one-sentence reasons",
  ].join("\n");
}

export function normalizeAiConcernMatch(
  value: unknown,
  context: ConcernMatchContext,
): AiConcernMatch {
  const payload = getObject(value) ?? {};
  const testsById = new Map(context.tests.map((test) => [test.id, test]));
  const fallbackTests = context.tests.slice(0, 3).map((test) => ({
    id: test.id,
    title: test.title,
    reason:
      context.locale === "ko"
        ? "현재 고민을 가볍게 정리해 보기 좋은 테스트예요."
        : "A useful quiz for lightly reframing this concern.",
  }));

  const recommended = Array.isArray(payload.recommendedTests)
    ? payload.recommendedTests
        .map((item) => {
          const row = getObject(item);
          const id = typeof row?.id === "string" ? row.id : "";
          const test = testsById.get(id);
          if (!test) return null;
          return {
            id,
            title: test.title,
            reason: getText(
              row?.reason,
              context.locale === "ko"
                ? "이 고민과 연결되는 성향을 가볍게 확인할 수 있어요."
                : "It can help you compare the tendency behind this concern.",
              140,
            ),
          };
        })
        .filter(
          (item): item is { id: string; title: string; reason: string } =>
            Boolean(item),
        )
        .slice(0, 3)
    : [];

  return {
    title: getText(
      payload.title,
      context.locale === "ko" ? "고민 성향 매칭" : "Concern match",
      64,
    ),
    summary: getText(
      payload.summary,
      context.locale === "ko"
        ? "입력한 고민을 바탕으로 가볍게 읽을 수 있는 성향 포인트를 정리했어요."
        : "A light read on the tendency behind your note.",
      260,
    ),
    tendencyTags: getTextArray(
      payload.tendencyTags,
      context.locale === "ko" ? ["자기이해", "관계", "선택"] : ["self-read", "pattern"],
      4,
      24,
    ),
    recommendedTests: recommended.length > 0 ? recommended : fallbackTests,
    disclaimer:
      context.locale === "ko"
        ? "고민 매칭은 재미와 자기 이해를 위한 콘텐츠이며 상담이나 전문 조언을 대체하지 않습니다."
        : "Concern matching is for entertainment and self-reflection, not professional advice.",
  };
}
